/** @jsxImportSource @emotion/react */
import { useReducer, useContext, useRef, useEffect } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { useQuery } from '@tanstack/react-query';
import type { ChatResponseType, Chat , HcpSearchResult , KolSearchResult  } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ActiveChatSessionContext, SmartRoutingHcpContext } from './context';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { usePlaceholderStyles } from './Message.System.styles';
import { MessageWrapper } from './Message.System.Wrapper';
import { SmartRoutingHcpSelection } from './SmartRoutingHcpSelection';
import { SmartRoutingLocation } from './SmartRoutingLocation';
import { SmartRoutingResult } from './SmartRoutingResult';

// Smart Routing HCP Context Provider Component with Search
type SmartRoutingHcpProviderWithSearchProps = {
  children: React.ReactNode;
  startingLocation: Chat.QueryState.SmartRouting['startingLocation'];
  status: Chat.QueryState.SmartRouting['status'];
  brandIdentifier: string;
  searchIndex?: string;
  hcpId?: string | number;
};

export const SmartRoutingHcpProviderWithSearch = ({
  children,
  startingLocation,
  status,
  brandIdentifier,
  searchIndex,
  hcpId,
}: SmartRoutingHcpProviderWithSearchProps) => {

  const apiClient = useChatApiClient();

  // fetching hcp if passed in to container (when searchIndex is provided)
  const { data: fetchedHcp = null } = useQuery({
    queryKey: ['hcp-by-id', hcpId, searchIndex],
    queryFn: async (): Promise<HcpSearchResult | null> => {
      if (!hcpId || !searchIndex) return null;

      try {
        const hcpIdNumber = typeof hcpId === 'string' ? parseInt(hcpId, 10) : hcpId;
        if (!isNaN(hcpIdNumber)) {
          const result = await apiClient.hcp.getHcpProfile({ hcpId: hcpIdNumber, searchIndex });
          return result.profile;
        }
      } catch (error) {
        console.error('Failed to fetch HCP by ID:', error);
      }
      return null;
    },
    enabled: !!hcpId && !!searchIndex && (!status || status === 'select-location'),
  });

  // Separate query for location-based HCP/KOL search
  const { data: hcpResults = [], isLoading: isSearchLoading, isFetched: hasSearchCompleted } = useQuery({
    queryKey: ['hcp-search', startingLocation?.latitude, startingLocation?.longitude, brandIdentifier, searchIndex],
    queryFn: async (): Promise<Array<KolSearchResult | HcpSearchResult>> => {
      if (!startingLocation?.latitude || !startingLocation?.longitude) {
        return [];
      }
      // Use HCP search if searchIndex is provided, otherwise use KOL search
      if (searchIndex) {
        const result = await apiClient.search.searchHcpsByLocation({
          lat: startingLocation.latitude,
          lon: startingLocation.longitude,
          brandIdentifier,
          searchIndex,
        });
        return result.results;
      } else {
        const result = await apiClient.search.searchKols({
          lat: startingLocation.latitude,
          lon: startingLocation.longitude,
          brandIdentifier,
        });
        return result.results;
      }
    },
    enabled: !!startingLocation && (status === 'select-hcps' || status === 'submit-route' || status === 'route-generated'),
  });

  return (
    <SmartRoutingHcpContext.Provider value={{
      searchResults: hcpResults,
      fetchedHcp,
      isSearchLoading,
      hasSearchCompleted,
      startingLocation,
    }}>
      {children}
    </SmartRoutingHcpContext.Provider>
  );
};

type ExtendedSmartRoutingState = Chat.QueryState.Value<ChatResponseType.SmartRouting> & {
  error?: string;
};

export type SmartRoutingAction =
  | { type: 'select-location' }
  | { type: 'submit-location'; payload: { startingLocation: ExtendedSmartRoutingState['startingLocation'] } }
  | { type: 'select-hcps' }
  | { type: 'submit-route'; payload: { selectedHcps: number[] } }
  | { type: 'route-generated'; payload: { routingResult: NonNullable<ExtendedSmartRoutingState['routingResult']> } }
  | { type: 'set-error'; payload: string }
  | { type: 'reset' };

// Reducer function
const smartRoutingReducer = (
  state: ExtendedSmartRoutingState,
  action: SmartRoutingAction,
): ExtendedSmartRoutingState => {
  switch (action.type) {
    case 'submit-location':
      return {
        ...state,
        status: 'submit-location',
        startingLocation: action.payload.startingLocation,
        selectedHcps: null,
        routingResult: null,
        error: undefined,
      };
    case 'select-location':
      return {
        ...state,
        status: 'select-location',
      };
    case 'select-hcps':
      return {
        ...state,
        status: 'select-hcps',
      };
    case 'submit-route':
      return {
        ...state,
        status: 'submit-route',
        selectedHcps: action.payload.selectedHcps,
      };
    case 'route-generated':
      return {
        ...state,
        status: 'route-generated',
        routingResult: action.payload.routingResult,
      };
    case 'set-error':
      return {
        ...state,
        error: action.payload,
      };
    default:
      return state;
  }
};

type Props = {
  item: Chat.QueryResponse;
  state: Chat.QueryState.Value<ChatResponseType.SmartRouting>;
  queryId: number;
  queryIdentifier: string;
  hcp?: KolSearchResult | null;
};

export const SmartRoutingContainer = (props: Props) => {
  const hcp = props.hcp;
  const startingLocation = hcp ? {
    type: 'hcp' as const,
    id: hcp.id,
    name: hcp.name,
    latitude: hcp.locationCoords.latitude,
    longitude: hcp.locationCoords.longitude,
  } : props.state.startingLocation;
  const [state, dispatch] = useReducer(
    smartRoutingReducer,
    {
      ...props.state,
      status: props.state.status ?? 'select-location',
      hcpId: hcp?.id ? String(hcp.id) : null,
      startingLocation: startingLocation ?? null,
      error: undefined,
    },
  );

  const chat = useContext(ActiveChatSessionContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const placeholderStyles = usePlaceholderStyles();

  const apiClient = useChatApiClient();

  const hcpSelectionRef = useRef<HTMLDivElement>(null);
  const routingResultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (state.status === 'select-hcps' && hcpSelectionRef.current) {
      setTimeout(() => {
        hcpSelectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [state.status]);

  useEffect(() => {
    if (state.status === 'route-generated' && routingResultRef.current) {
      setTimeout(() => {
        routingResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [state.status]);

  const handleLocationSubmitted = async (startingLocation: ExtendedSmartRoutingState['startingLocation']) => {
    if (startingLocation) {
      try {
        await apiClient.queries.updateQuerySmartRouting({
          chatInstanceId: chat.id,
          queryIdentifier: props.queryIdentifier,
          smartRoutingState: {
            ...state,
            status: 'submit-location',
            startingLocation,
            selectedHcps: null,
            routingResult: null,
          },
        });
        dispatch({ type: 'select-hcps' });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'API call failed';
        dispatch({ type: 'set-error', payload: errorMessage });
      }
    }
  };

  const handleRouteSubmitted = async (selectedHcps: number[]) => {
    if (selectedHcps.length > 0) {
      try {
        const newState = await apiClient.queries.updateQuerySmartRouting({
          chatInstanceId: chat.id,
          queryIdentifier: props.queryIdentifier,
          smartRoutingState: {
            ...state,
            status: 'submit-route',
            selectedHcps,
            routingResult: null,
          },
        });
        dispatch({
          type: 'route-generated',
          payload: { routingResult: newState.smartRoutingState.routingResult },
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'API call failed';
        dispatch({ type: 'set-error', payload: errorMessage });
      }
    }
  };

  return (
    <SmartRoutingHcpProviderWithSearch
      startingLocation={state.startingLocation}
      status={state.status}
      brandIdentifier={brandIdentifier}
      searchIndex={state.searchIndex}
      hcpId={props.state.hcpId}
    >
      <div>
        {!props.state.hideLocationForm && (
          <MessageWrapper>
            <BasicSystemUserMessage>
              <SmartRoutingLocation
                value={state}
                hcp={props.hcp}
                disabled={state.status !== 'select-location'}
                dispatch={dispatch}
                onLocationSubmitted={handleLocationSubmitted}
              />
            </BasicSystemUserMessage>
          </MessageWrapper>
        )}
        {state.status === 'select-hcps' ? (
          <MessageWrapper>
            <div ref={hcpSelectionRef}>
              <SmartRoutingHcpSelection
                state={state}
                dispatch={dispatch}
                queryIdentifier={props.queryIdentifier}
                onRouteSubmitted={handleRouteSubmitted}
              />
            </div>
          </MessageWrapper>
        ) : null}
        {state.status === 'submit-route' ? (
          <MessageWrapper>
            <BasicSystemUserMessage>
              <div css={placeholderStyles.skeleton}>
                <Skeleton
                  css={placeholderStyles.skeletonItem}
                  variant="text"
                  width="95%" />
                <Skeleton
                  css={placeholderStyles.skeletonItem}
                  variant="text"
                  width="80%" />
                <Skeleton
                  css={placeholderStyles.skeletonItem}
                  variant="text"
                  width="65%" />
              </div>
            </BasicSystemUserMessage>
          </MessageWrapper>
        ) : null}
        {state.status === 'route-generated' && state.routingResult ? (
          <MessageWrapper>
            <div ref={routingResultRef}>
              <BasicSystemUserMessage>
                <SmartRoutingResult
                  state={state}
                  dispatch={dispatch}
                />
              </BasicSystemUserMessage>
            </div>
          </MessageWrapper>
        ) : null}
      </div>
    </SmartRoutingHcpProviderWithSearch>
  );
};